import ArticleLayout from '@/pages/article/components/ArticleLayout';

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Was ist der OOPBUY QC Guide?', acceptedAnswer: { '@type': 'Answer', text: 'Der OOPBUY QC Guide erklärt, wie du Qualitätskontrollbilder (QC-Bilder) von chinesischen Shopping-Agenten richtig analysierst und interpretierst.' } },
    { '@type': 'Question', name: 'Worauf muss ich bei QC-Bildern achten?', acceptedAnswer: { '@type': 'Answer', text: 'Achte auf Nähte, Materialqualität, Farbgenauigkeit, Logos, Größenangaben und allgemeine Verarbeitung. Vergleiche die QC-Bilder mit den Verkäuferbildern.' } },
    { '@type': 'Question', name: 'Was mache ich wenn die QC-Bilder nicht gut aussehen?', acceptedAnswer: { '@type': 'Answer', text: 'Du kannst den Artikel zurücksenden oder umtauschen lassen. Die meisten Shopping-Agenten bieten einen QC-Check mit Rückgaberecht an.' } },
    { '@type': 'Question', name: 'Sind QC-Bilder immer verfügbar?', acceptedAnswer: { '@type': 'Answer', text: 'Nicht alle Agenten bieten QC-Bilder standardmäßig an. OOPBUY bietet diesen Service jedoch bei jeder Bestellung an.' } },
    { '@type': 'Question', name: 'Kosten QC-Bilder extra?', acceptedAnswer: { '@type': 'Answer', text: 'Bei den meisten Agenten sind einfache QC-Bilder kostenlos. Detaillierte HD-Bilder oder Videos können zusätzliche Kosten verursachen.' } },
  ],
});

export default function QCGuidePage() {
  return (
    <ArticleLayout
      title="OOPBUY QC Guide 2026 – Qualitätskontrolle für deutsche Käufer einfach erklärt"
      description="Lerne Schritt für Schritt, wie du Qualitätskontrollbilder (QC-Bilder) richtig analysierst, Fehlkäufe vermeidest und sicher bei chinesischen Shopping-Agenten einkaufst. Der komplette QC Guide für OOPBUY Nutzer in Deutschland."
      heroImage="https://readdy.ai/api/search-image?query=Professional%20product%20inspection%20scene%20with%20a%20person%20examining%20a%20white%20sneaker%20using%20a%20magnifying%20glass%20on%20a%20clean%20oak%20desk%2C%20multiple%20QC%20photo%20prints%20spread%20across%20the%20table%2C%20soft%20daylight%20from%20window%2C%20warm%20neutral%20office%20environment%2C%20editorial%20quality%2C%20shallow%20depth%20of%20field&width=1200&height=500&seq=article-qc-hero&orientation=landscape"
      heroAlt="OOPBUY QC Guide Qualitätskontrolle"
      breadcrumbLabel="QC Guide"
      schemaMarkup={FAQ_SCHEMA}
    >
      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          1. Was ist Quality Control (QC) bei OOPBUY?
        </h2>
        <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
          <p>
            <strong>Quality Control</strong>, kurz QC, ist einer der wichtigsten Schritte beim Einkauf über chinesische Shopping-Agenten wie OOPBUY. Sobald deine bestellten Artikel im Lager des Agenten ankommen, werden <strong>QC-Bilder</strong> (Qualitätskontrollfotos) erstellt. Diese Bilder zeigen den tatsächlichen Zustand des Produkts – und nicht die oft bearbeiteten Verkäuferfotos.
          </p>
          <p>
            Für <strong>deutsche Käufer</strong> ist der QC-Prozess besonders wertvoll, da die Rücksendung von Artikeln nach China sehr teuer und zeitaufwendig wäre. Mit guten QC-Bildern kannst du Fehlkäufe erkennen, bevor das Paket überhaupt Deutschland erreicht.
          </p>
          <p>
            Der QC-Prozess bei OOPBUY umfasst in der Regel drei bis fünf Fotos pro Artikel aus verschiedenen Winkeln. Auf Wunsch können auch <strong>Detailaufnahmen</strong> von bestimmten Bereichen wie Logos, Nähten oder Etiketten angefordert werden. Manche Agenten bieten sogar HD-Fotos und kurze Videos gegen einen kleinen Aufpreis an.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          2. Worauf du bei QC-Bildern achten musst
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[
            { title: 'Nähte & Verarbeitung', desc: 'Überprüfe die Nähte auf gerade Linien und saubere Verarbeitung. Lose Fäden oder schiefe Nähte sind ein Warnsignal.', icon: 'ri-ruler-line' },
            { title: 'Materialqualität', desc: 'Achte auf die Textur und den Glanz des Materials. Billige Stoffe sehen auf QC-Bildern oft anders aus als auf Verkäuferfotos.', icon: 'ri-shirt-line' },
            { title: 'Farbgenauigkeit', desc: 'Vergleiche die Farbe mit dem Verkäuferbild. Unterschiedliche Beleuchtung kann Farben verfälschen – frage nach Bildern bei Tageslicht.', icon: 'ri-palette-line' },
            { title: 'Logos & Details', desc: 'Prüfe Logos, Schriftzüge und Markenzeichen genau. Unscharfe oder falsch positionierte Logos deuten auf Fälschungen hin.', icon: 'ri-zoom-in-line' },
            { title: 'Größenangaben', desc: 'Lass ein Foto mit Maßband machen, um die tatsächlichen Maße zu überprüfen. Asiatische Größen weichen oft stark ab.', icon: 'ri-ruler-2-line' },
            { title: 'Gesamteindruck', desc: 'Betrachte das Produkt als Ganzes. Sieht es hochwertig aus? Entspricht es deinen Erwartungen? Vertraue deinem Bauchgefühl.', icon: 'ri-eye-line' },
          ].map((item) => (
            <div key={item.title} className="bg-background-100 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-accent-600 text-lg`} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          3. QC-Prozess Schritt für Schritt
        </h2>
        <div className="space-y-4">
          {[
            { step: '1', title: 'Bestellung aufgeben', desc: 'Gib deine Bestellung wie gewohnt über OOPBUY auf. Der Agent bestellt die Artikel beim chinesischen Verkäufer.' },
            { step: '2', title: 'Wareneingang abwarten', desc: 'Sobald die Artikel im OOPBUY-Lager eintreffen, wirst du benachrichtigt. Dies dauert in der Regel 2-5 Werktage nach Verkäuferversand.' },
            { step: '3', title: 'QC-Bilder prüfen', desc: 'OOPBUY erstellt automatisch QC-Bilder. Schaue dir jedes Bild genau an und vergleiche es mit der Produktbeschreibung.' },
            { step: '4', title: 'Entscheidung treffen', desc: 'Akzeptiere das Produkt wenn alles passt, oder lehne es ab und fordere eine Rückerstattung oder einen Umtausch an.' },
            { step: '5', title: 'Versand nach Deutschland', desc: 'Nach der Freigabe wird dein Artikel für den internationalen Versand vorbereitet und auf den Weg nach Deutschland geschickt.' },
          ].map((item) => (
            <div key={item.step} className="flex gap-4 bg-background-100 rounded-xl p-5">
              <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">{item.step}</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          4. Häufige QC-Probleme und Lösungen
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-200">
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Problem</th>
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Ursache</th>
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Lösung</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Falsche Farbe', 'Beleuchtung im QC-Studio', 'Tageslichtbilder anfordern'],
                ['Falsche Größe', 'Falsche Größentabelle', 'Maßband-Foto verlangen'],
                ['Flecken/Schäden', 'Transportschaden', 'Rückgabe einleiten'],
                ['Logo fehlt/anders', 'Fälschung', 'Sofort Ablehnung'],
                ['Material anders', 'Minderwertiges Material', 'Detailaufnahme anfordern'],
              ].map((row, i) => (
                <tr key={i} className="border-b border-secondary-100">
                  <td className="py-3 pr-4 font-medium text-foreground-800">{row[0]}</td>
                  <td className="py-3 pr-4 text-foreground-600">{row[1]}</td>
                  <td className="py-3 pr-4 text-accent-700">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
          5. Häufig gestellte Fragen zum QC
        </h2>
        <div className="space-y-3">
          {[
            { q: 'Was ist der OOPBUY QC Guide?', a: 'Der OOPBUY QC Guide ist eine umfassende Anleitung, die deutschen Käufern hilft, Qualitätskontrollbilder von chinesischen Shopping-Agenten richtig zu analysieren und Fehlkäufe zu vermeiden.' },
            { q: 'Worauf muss ich bei QC-Bildern besonders achten?', a: 'Die wichtigsten Punkte sind Nähte und Verarbeitung, Materialqualität, Farbgenauigkeit im Vergleich zum Verkäuferbild, korrekte Logos und Schriftzüge, sowie die tatsächlichen Maße des Produkts.' },
            { q: 'Was mache ich wenn die QC-Bilder Mängel zeigen?', a: 'Du kannst den Artikel zurücksenden oder umtauschen lassen. OOPBUY bietet einen QC-Service mit Rückgaberecht an. Wichtig: Handle schnell, da Rückgabefristen oft nur 48-72 Stunden betragen.' },
            { q: 'Sind QC-Bilder bei OOPBUY immer kostenlos?', a: 'Standard-QC-Bilder (3-5 Fotos) sind bei den meisten Agenten kostenlos im Service enthalten. HD-Bilder, Detailaufnahmen oder kurze Videos kosten meist einen kleinen Aufpreis von 1-3 Yuan pro Artikel.' },
            { q: 'Kann ich zusätzliche QC-Bilder anfordern?', a: 'Ja, die meisten Agenten ermöglichen es, spezifische Detailaufnahmen anzufordern. Du kannst zum Beispiel ein Nahaufnahme-Foto des Logos, der Innensohle oder des Etiketts verlangen.' },
          ].map((faq, i) => (
            <details key={i} className="bg-background-100 rounded-xl group">
              <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                {faq.q}
                <i className="ri-add-line text-foreground-400 group-open:hidden" />
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
              </summary>
              <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}